import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Scale, FileText, ShieldOff, Cookie, Copyright } from "lucide-react";
import { POLICIES, POLICY_ORDER, POLICY_META } from "@/data/policies";
import Footer from "@/components/landing/Footer";

const ICONS = {
  "privacy-policy": Scale,
  "terms-of-use": FileText,
  "do-not-sell": ShieldOff,
  "cookie-policy": Cookie,
  "dmca-policy": Copyright,
};

/**
 * Renders any legal policy from `/src/data/policies.js` based on the :slug
 * URL param. Bold text uses **markdown-lite** — inline `**...**` becomes bold.
 */
const renderRich = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={i} className="text-white font-semibold">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{p}</span>;
  });
};

const LegalPage = () => {
  const { slug } = useParams();
  const policy = POLICIES[slug];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!policy) {
    return <Navigate to="/legal/privacy-policy" replace />;
  }
  const Icon = ICONS[slug] || FileText;

  return (
    <div
      data-testid={`legal-page-${slug}`}
      className="min-h-screen bg-[#05050a] text-white font-sans relative overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[420px] h-[420px] rounded-full bg-[#6b21d9]/15 blur-[160px]" />
        <div className="absolute top-[30%] right-[-8%] w-[380px] h-[380px] rounded-full bg-[#00f0ff]/10 blur-[140px]" />
      </div>

      <div className="relative max-w-[1080px] mx-auto px-5 md:px-10 py-12 md:py-20">
        {/* Back link */}
        <Link
          to="/"
          data-testid="legal-back-home"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-white/50 hover:text-[#00f0ff] transition-colors"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 md:mt-10 grid md:grid-cols-[auto_1fr] items-start gap-6"
        >
          <div className="w-14 h-14 rounded-2xl border border-white/10 bg-white/[0.03] flex items-center justify-center backdrop-blur-md">
            <Icon size={22} className="text-[#00f0ff]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#00f0ff] mb-3">
              Legal · {POLICY_META[slug]?.label}
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              {policy.title}
              <span className="text-[#ff0055]">.</span>
            </h1>
            <p className="text-[#a395c3] text-base mt-4 max-w-2xl leading-relaxed">
              {policy.kicker}
            </p>
            <p className="text-white/40 text-xs mt-4 font-mono-tight uppercase tracking-[0.22em]">
              Effective {policy.effective}
            </p>
          </div>
        </motion.header>

        <div className="mt-10 grid md:grid-cols-[220px_1fr] gap-10">
          {/* Sticky nav to other policies */}
          <aside className="hidden md:block sticky top-8 self-start">
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/40 mb-3">
              All policies
            </div>
            <ul className="space-y-1.5">
              {POLICY_ORDER.map((s) => {
                const active = s === slug;
                return (
                  <li key={s}>
                    <Link
                      to={`/legal/${s}`}
                      data-testid={`legal-nav-${s}`}
                      className={`block text-sm py-1.5 pl-3 border-l-2 transition-all ${
                        active
                          ? "border-[#00f0ff] text-white"
                          : "border-white/5 text-white/50 hover:text-white/80 hover:border-white/20"
                      }`}
                    >
                      {POLICY_META[s].label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Body */}
          <article className="prose prose-invert max-w-none">
            {policy.sections.map((s, i) => (
              <motion.section
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: 0.04 * Math.min(i, 8) }}
                className="mb-8"
              >
                <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight text-white mb-3">
                  {s.heading}
                </h2>
                <div className="space-y-3">
                  {s.body.map((para, j) => (
                    <p
                      key={j}
                      className="text-[#c8bde4] text-[15px] leading-[1.75]"
                    >
                      {renderRich(para)}
                    </p>
                  ))}
                </div>
              </motion.section>
            ))}
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LegalPage;
